/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { z } from 'zod';
import type { TFunction } from 'i18next';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';

export const RECURRENCE_OPTIONS = ['none', 'daily', 'weekly', 'monthly'] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_OPTIONS)[number];

/**
 * Builds the Zod validation schema for the event form with localized messages.
 * @param t - The translation function.
 * @returns The event form validation schema.
 */
export function createEventSchema(t: TFunction) {
  return z.object({
    title: z.string()
      .min(1, t('drawer.validation.titleRequired'))
      .max(FORM_FIELD_LIMITS.title),
    calendarId: z.string().min(1, t('drawer.validation.calendarRequired')),
    date: z.string().min(1, t('drawer.validation.dateRequired')),
    startTime: z.string().default(''),
    endTime: z.string().default(''),
    allDay: z.boolean().default(false),
    frequency: z.enum(RECURRENCE_OPTIONS).default('none'),
    description: z.string().max(FORM_FIELD_LIMITS.description).default(''),
  }).refine(
    (data) => data.allDay || data.startTime.length > 0,
    { message: t('drawer.validation.startTimeRequired'), path: ['startTime'] },
  ).refine(
    (data) => data.allDay || data.endTime.length > 0,
    { message: t('drawer.validation.endTimeRequired'), path: ['endTime'] },
  );
}

export const eventSchema = z.object({
  title: z.string().min(1).max(FORM_FIELD_LIMITS.title),
  calendarId: z.string().min(1),
  date: z.string().min(1),
  startTime: z.string().default(''),
  endTime: z.string().default(''),
  allDay: z.boolean().default(false),
  frequency: z.enum(RECURRENCE_OPTIONS).default('none'),
  description: z.string().max(FORM_FIELD_LIMITS.description).default(''),
}).refine(
  (data) => data.allDay || data.startTime.length > 0,
  { path: ['startTime'] },
).refine(
  (data) => data.allDay || data.endTime.length > 0,
  { path: ['endTime'] },
);

export type EventFormData = z.infer<typeof eventSchema>;

export const DEFAULT_VALUES: EventFormData = {
  title: '',
  calendarId: '',
  date: '',
  startTime: '',
  endTime: '',
  allDay: false,
  frequency: 'none',
  description: '',
};
